/**
 * The one place the web app knows about the Kortex Android kiosk.
 *
 * On an Android tablet the native shell injects `window.KortexAndroid`
 * (see android/…/web/WebAppBridge.kt). In every ordinary browser that object
 * simply does not exist, `isAndroidKiosk()` is false, and everything in the
 * app behaves exactly as it always has — nothing may assume Android.
 *
 * Async native operations use a requestId handshake: we generate an id, the
 * native side answers through `window.__kortexAndroidDeliver`, and the pending
 * Promise resolves. A request the native side never answers rejects after a
 * timeout instead of hanging a till forever.
 */

export interface AndroidDeviceInfo {
  manufacturer: string;
  model: string;
  androidVersion: string;
  sdkInt: number;
  appVersion: string;
  appVersionCode: number;
  environment: string;
  kioskActive: boolean;
}

export interface AndroidNetworkStatus {
  online: boolean;
  transport: string;
}

export interface AndroidPrintResult {
  ok: boolean;
  transport?: string;
  code?: string;
  message?: string;
}

export interface AndroidHardwareArea {
  status: 'SUPPORTED' | 'NOT_CONFIGURED' | 'NOT_SUPPORTED';
  detail: string;
}

export interface AndroidHardwareStatus {
  printer: AndroidHardwareArea;
  barcode: AndroidHardwareArea;
  cashDrawer: AndroidHardwareArea;
  paymentTerminal: AndroidHardwareArea;
  customerDisplay: AndroidHardwareArea;
}

/** The raw object Android injects; every method is synchronous on its side. */
interface NativeBridge {
  isAvailable(): boolean;
  getBridgeVersion(): string;
  getAppVersion(): string;
  getDeviceInfo(): string;
  getNetworkStatus(): string;
  getHardwareStatus(): string;
  printReceipt(requestId: string, payloadJson: string): void;
  printKot(requestId: string, payloadJson: string): void;
  printPage(requestId: string): void;
  scanBarcode(requestId: string): void;
  /** Customer-facing display (bridge >= 1.2; Phase 6, mock-only today). */
  showCustomerDisplay?(requestId: string, payloadJson: string): void;
  clearCustomerDisplay?(requestId: string): void;
  /** Local SQLite access for @kortex/pos-local (bridge ≥ 1.1; Phase 2). */
  database?(requestId: string, payloadJson: string): void;
  /**
   * Keystore-backed secret storage (bridge ≥ 1.3): values are encrypted with
   * an Android Keystore key, not left in WebView localStorage. Synchronous.
   * secureGet returns '' for a missing key.
   */
  secureGet?(key: string): string;
  secureSet?(key: string, value: string): boolean;
  secureRemove?(key: string): void;
  /**
   * Clears WebView data (bridge ≥ 1.3). scope "session": the HTTP cache and
   * form data, at sign-out. scope "all": also cookies, every web storage
   * area (localStorage, IndexedDB, OPFS) and the native secret store - the
   * device-wipe path.
   */
  clearWebData?(requestId: string, scope: string): void;
}

declare global {
  interface Window {
    KortexAndroid?: NativeBridge;
    __kortexAndroidDeliver?: (message: { id: string; result: string }) => void;
  }
}

const native = (): NativeBridge | undefined =>
  typeof window !== 'undefined' ? window.KortexAndroid : undefined;

/** Whether this page is running inside the Kortex Android kiosk shell. */
export const isAndroidKiosk = (): boolean => native() !== undefined;

export const getBridgeVersion = (): string | null => native()?.getBridgeVersion() ?? null;
export const getAndroidAppVersion = (): string | null => native()?.getAppVersion() ?? null;

export function getDeviceInfo(): AndroidDeviceInfo | null {
  const bridge = native();
  if (!bridge) return null;
  try {
    return JSON.parse(bridge.getDeviceInfo()) as AndroidDeviceInfo;
  } catch {
    return null;
  }
}

export function getNetworkStatus(): AndroidNetworkStatus | null {
  const bridge = native();
  if (!bridge) return null;
  try {
    return JSON.parse(bridge.getNetworkStatus()) as AndroidNetworkStatus;
  } catch {
    return null;
  }
}

export function getHardwareStatus(): AndroidHardwareStatus | null {
  const bridge = native();
  if (!bridge) return null;
  try {
    return JSON.parse(bridge.getHardwareStatus()) as AndroidHardwareStatus;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Async request/response plumbing
// ---------------------------------------------------------------------------

const REQUEST_TIMEOUT_MS = 30_000;
let nextRequestId = 0;
const pending = new Map<
  string,
  { resolve: (raw: string) => void; timer: ReturnType<typeof setTimeout> }
>();

/** Lowest level: send one request, get the native side's raw result JSON. */
function requestRaw(invoke: (bridge: NativeBridge, requestId: string) => void): Promise<string> {
  const bridge = native();
  if (!bridge) {
    return Promise.resolve(
      JSON.stringify({ ok: false, code: 'NO_BRIDGE', message: 'Not running on Android' }),
    );
  }
  const id = `req_${Date.now()}_${nextRequestId++}`;
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      resolve(
        JSON.stringify({ ok: false, code: 'TIMEOUT', message: 'The Android app did not respond' }),
      );
    }, REQUEST_TIMEOUT_MS);
    pending.set(id, { resolve, timer });
    try {
      invoke(bridge, id);
    } catch (error) {
      clearTimeout(timer);
      pending.delete(id);
      resolve(JSON.stringify({ ok: false, code: 'BRIDGE_ERROR', message: String(error) }));
    }
  });
}

async function request(
  invoke: (bridge: NativeBridge, requestId: string) => void,
): Promise<AndroidPrintResult> {
  const raw = await requestRaw(invoke);
  try {
    return JSON.parse(raw) as AndroidPrintResult;
  } catch {
    return { ok: false, code: 'BAD_RESULT', message: 'Unreadable result from Android' };
  }
}

function deliver(message: { id: string; result: string }): void {
  const entry = pending.get(message.id);
  if (!entry) return;
  pending.delete(message.id);
  clearTimeout(entry.timer);
  entry.resolve(message.result);
}

// ---------------------------------------------------------------------------
// Operations
// ---------------------------------------------------------------------------

export interface ThermalPrintPayload {
  /** The final fixed-width render from @kortex/shared (renderReceipt/renderKot). */
  text: string;
  widthMm: 58 | 80;
  copies?: number;
  cut?: boolean;
  openDrawer?: boolean;
}

export const printReceipt = (payload: ThermalPrintPayload): Promise<AndroidPrintResult> =>
  request((bridge, id) => bridge.printReceipt(id, JSON.stringify(payload)));

export const printKot = (payload: ThermalPrintPayload): Promise<AndroidPrintResult> =>
  request((bridge, id) => bridge.printKot(id, JSON.stringify(payload)));

/** Prints the current page via Android's print framework (A4 flows). */
export const printPage = (): Promise<AndroidPrintResult> =>
  request((bridge, id) => bridge.printPage(id));

export const scanBarcode = (): Promise<AndroidPrintResult> =>
  request((bridge, id) => bridge.scanBarcode(id));

/**
 * Shows up to two lines on the customer-facing display (mock-only today -
 * see android/…/hardware/CustomerDisplayManager.kt). On an older APK
 * without the method, or outside the kiosk, this resolves to a structured
 * NOT_CONFIGURED/NO_BRIDGE result rather than throwing - callers treat the
 * customer display as a best-effort peripheral, never something a sale
 * waits on.
 */
export const showCustomerDisplay = (lines: string[]): Promise<AndroidPrintResult> => {
  const bridge = native();
  if (bridge && typeof bridge.showCustomerDisplay !== 'function') {
    return Promise.resolve({ ok: false, code: 'UNSUPPORTED', message: 'No customer-display bridge' });
  }
  return request((b, id) => b.showCustomerDisplay?.(id, JSON.stringify({ lines })));
};

export const clearCustomerDisplay = (): Promise<AndroidPrintResult> => {
  const bridge = native();
  if (bridge && typeof bridge.clearCustomerDisplay !== 'function') {
    return Promise.resolve({ ok: false, code: 'UNSUPPORTED', message: 'No customer-display bridge' });
  }
  return request((b, id) => b.clearCustomerDisplay?.(id));
};

/**
 * Local database access (bridge ≥ 1.1). The payload/answer contract belongs
 * to `@kortex/pos-local`'s BridgeSqliteDriver, which Phase 3 wires up as
 * `new BridgeSqliteDriver(databaseRequest)`; this function only moves the
 * strings. On an older APK without the method, callers get a structured
 * UNSUPPORTED answer instead of a crash.
 */
export const databaseRequest = (payloadJson: string): Promise<string> => {
  const bridge = native();
  if (bridge && typeof bridge.database !== 'function') {
    return Promise.resolve(
      JSON.stringify({
        ok: false,
        code: 'UNSUPPORTED',
        message: 'This Android app version has no local database bridge',
      }),
    );
  }
  return requestRaw((b, id) => b.database?.(id, payloadJson));
};

/**
 * The native secret store, when this APK has one (bridge ≥ 1.3) - `null`
 * otherwise, and callers fall back to localStorage (lib/deviceCredential.ts).
 */
export function nativeSecureStore(): {
  get(key: string): string | null;
  set(key: string, value: string): boolean;
  remove(key: string): void;
} | null {
  const bridge = native();
  if (!bridge || typeof bridge.secureGet !== 'function' || typeof bridge.secureSet !== 'function') return null;
  return {
    get: (key) => {
      try {
        return bridge.secureGet?.(key) || null;
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      try {
        return bridge.secureSet?.(key, value) === true;
      } catch {
        return false;
      }
    },
    remove: (key) => {
      try {
        bridge.secureRemove?.(key);
      } catch {
        // best-effort
      }
    },
  };
}

/**
 * Asks the Android shell to clear WebView data - see NativeBridge.clearWebData.
 * Resolves (never rejects) once the shell reports back, or straight away on
 * an older APK or outside the kiosk.
 */
export async function clearNativeWebData(scope: 'session' | 'all'): Promise<AndroidPrintResult> {
  const bridge = native();
  if (!bridge || typeof bridge.clearWebData !== 'function') {
    return { ok: false, code: 'UNSUPPORTED', message: 'This Android app version cannot clear its web data' };
  }
  return request((b, id) => b.clearWebData?.(id, scope));
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

/**
 * Installs the bridge plumbing. Called once from main.tsx; a no-op in normal
 * browsers, so it can never affect desktop behaviour.
 *
 * Inside the kiosk it also routes `window.print()` to the native print
 * framework, because WebView silently ignores `window.print()` — this keeps
 * the app's nine existing print buttons (invoices, payslips, QR sheets,
 * reports) working with no changes to any of them.
 */
export function initAndroidBridge(): void {
  if (typeof window === 'undefined') return;
  window.__kortexAndroidDeliver = deliver;
  if (!isAndroidKiosk()) return;

  const nativePrint = () => {
    void printPage();
  };
  window.print = nativePrint;
}
